import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { useDomainStore } from '@/hooks/useStores';
import { useAuth } from '@/hooks/useAuth';
import { ACCOUNT_DELETE_CONFIRMATION } from '@/types/account-deletion';
import type { AccountDeletionImpactResponse } from 'we-tree-api-client/v2';
import '@/styles/form-select.css';
import './delete-account-screen.css';

const DeleteAccountScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [impact, setImpact] = useState<AccountDeletionImpactResponse | null>(null);
  const [successors, setSuccessors] = useState<Record<string, string>>({});
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await domainStore.getAccountDeletionImpact();
        if (!cancelled) {
          setImpact(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('deleteAccount.loadPreviewFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domainStore, t]);

  const allSuccessorsSelected =
    !impact || impact.familiesRequiringSuccessor.every((f) => Boolean(successors[f.familyId]));

  const confirmationOk = confirmation.trim() === ACCOUNT_DELETE_CONFIRMATION;
  const canSubmit = Boolean(impact) && allSuccessorsSelected && confirmationOk && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !impact) return;

    setSubmitting(true);
    setError(null);
    try {
      await domainStore.deleteAccount({
        confirmation: ACCOUNT_DELETE_CONFIRMATION,
        successors: impact.familiesRequiringSuccessor.map((f) => ({
          familyId: f.familyId,
          newOwnerUserId: successors[f.familyId]
        }))
      });
      await signOut();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('deleteAccount.deletionFailed'));
      setSubmitting(false);
    }
  };

  return (
    <div className="delete-account-screen">
      <header className="delete-account-screen__header">
        <p className="delete-account-screen__eyebrow">
          <Link to="/profile" className="delete-account-screen__back">
            {t('deleteAccount.breadcrumbAccount')}
          </Link>
          <span aria-hidden="true"> / </span>
          {t('deleteAccount.breadcrumbDelete')}
        </p>
        <h1 className="delete-account-screen__title">{t('deleteAccount.title')}</h1>
        <p className="delete-account-screen__lede">{t('deleteAccount.lede')}</p>
      </header>

      {loading && <p className="delete-account-screen__status">{t('deleteAccount.checkingFamilies')}</p>}

      {!loading && error && !impact && (
        <div className="delete-account-screen__message delete-account-screen__message--error" role="alert">
          {error}
        </div>
      )}

      {!loading && impact && (
        <form className="delete-account-screen__form" onSubmit={handleSubmit}>
          {impact.familiesRequiringSuccessor.length > 0 && (
            <section className="delete-account-screen__section" aria-labelledby="successor-heading">
              <h2 id="successor-heading" className="delete-account-screen__section-title">
                {t('deleteAccount.chooseOwnerTitle')}
              </h2>
              <p className="delete-account-screen__section-copy">{t('deleteAccount.chooseOwnerCopy')}</p>
              <ul className="delete-account-screen__list">
                {impact.familiesRequiringSuccessor.map((family) => (
                  <li key={family.familyId} className="delete-account-screen__list-item">
                    <label className="delete-account-screen__label" htmlFor={`successor-${family.familyId}`}>
                      {family.familyName}
                    </label>
                    <Select.Root
                      value={successors[family.familyId] || undefined}
                      onValueChange={(value) => setSuccessors((prev) => ({ ...prev, [family.familyId]: value }))}
                    >
                      <Select.Trigger
                        id={`successor-${family.familyId}`}
                        className="form-select-trigger"
                        aria-label={t('deleteAccount.newOwnerAria', { familyName: family.familyName })}
                      >
                        <Select.Value placeholder={t('deleteAccount.selectMember')} />
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                          {family.candidates.map((c) => (
                            <Select.Item key={c.userId} value={c.userId} className="form-select-item">
                              <Select.ItemText>{(c.nickname || c.email) + ` (${c.role})`}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {impact.familiesToArchive.length > 0 && (
            <section className="delete-account-screen__section" aria-labelledby="archive-heading">
              <h2 id="archive-heading" className="delete-account-screen__section-title">
                {t('deleteAccount.archiveTitle')}
              </h2>
              <p className="delete-account-screen__section-copy">{t('deleteAccount.archiveCopy')}</p>
              <ul className="delete-account-screen__simple-list">
                {impact.familiesToArchive.map((f) => (
                  <li key={f.familyId}>{f.familyName}</li>
                ))}
              </ul>
            </section>
          )}

          {impact.familiesToLeave.length > 0 && (
            <section className="delete-account-screen__section" aria-labelledby="leave-heading">
              <h2 id="leave-heading" className="delete-account-screen__section-title">
                {t('deleteAccount.leaveTitle')}
              </h2>
              <p className="delete-account-screen__section-copy">{t('deleteAccount.leaveCopy')}</p>
              <ul className="delete-account-screen__simple-list">
                {impact.familiesToLeave.map((f) => (
                  <li key={f.familyId}>{f.familyName}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="delete-account-screen__section" aria-labelledby="confirm-heading">
            <h2 id="confirm-heading" className="delete-account-screen__section-title">
              {t('deleteAccount.confirmTitle')}
            </h2>
            <p className="delete-account-screen__section-copy">
              <Trans
                i18nKey="deleteAccount.confirmCopy"
                ns="screens"
                values={{ confirmation: ACCOUNT_DELETE_CONFIRMATION }}
                components={{ strong: <strong /> }}
              />
            </p>
            <label className="delete-account-screen__label" htmlFor="delete-confirmation">
              {t('deleteAccount.confirmationLabel')}
            </label>
            <input
              id="delete-confirmation"
              className="delete-account-screen__input"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </section>

          {error && (
            <div className="delete-account-screen__message delete-account-screen__message--error" role="alert">
              {error}
            </div>
          )}

          <div className="delete-account-screen__actions">
            <Link to="/profile" className="btn btn--secondary">
              {tCommon('cancel')}
            </Link>
            <button type="submit" className="btn btn--danger" disabled={!canSubmit}>
              {submitting ? t('deleteAccount.deleting') : tForms('actions.deleteMyAccount')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
});

export default DeleteAccountScreen;
